import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Text,
  Button,
  Cross,
  Checkbox,
  Modal,
  ModalHeader,
  ModalDescription,
  Grid,
  ModalContent,
  Row,
  Column,
  Form,
  List,
} from '@cashfree-intl/coherent';
import _pick from 'lodash/pick';

// Services
import { addNewRecipient } from 'services/settings';

// Utils
import isFormValid from 'utils/isFormValid';
import { emailValidation } from 'utils/formValidation';

// Constants
import { KNOW_MORE } from 'constants/urls';
import { REQUIRED_FIELDS } from '../constants';

// Styled
import { BtnContainer } from 'styled/common';

const AddRecipientModal = ({ data, selectedAccountId, onSubmit, onClose }) => {
  const [formObj, setFormObj] = useState({});
  const [errorObj, setErrorObj] = useState({});
  const [selectedIds, setSelectedIds] = useState(Object.keys(data));

  const addRecipient = async e => {
    e.preventDefault();

    const queryObj = { selectedAccountId };

    const recipients = formObj.emails.split(',').map(email => email.trim());

    const selectedData = _pick(data, selectedIds);

    const body = {
      product: 'PAYOUT',
      notifType: Object.keys(selectedData).map(
        id => selectedData[id].notifType,
      ),
      notifSubType: Object.keys(selectedData).map(
        id => selectedData[id].notifSubType,
      ),
      recipients,
    };

    const response = await addNewRecipient(queryObj, body);
    const status = response.status || response.error.status;

    onSubmit(status);
  };

  const handleToggle = id => {
    setSelectedIds(prevIds =>
      prevIds.includes(id)
        ? prevIds.filter(prevId => id !== prevId)
        : prevIds.concat(id),
    );
  };

  const handleChange = (e, { name, value }) => {
    let error;

    switch (name) {
      case 'emails':
        {
          const emailArray = value.split(',');

          if (emailArray.length > 10) {
            error = 'Exceeding maximum limit of recipients allowed';
          } else {
            const invalidEmails = emailArray.filter(email =>
              emailValidation(email),
            );

            if (invalidEmails.length > 1) {
              error = 'Invalid emails';
            } else if (invalidEmails.length === 1) {
              error = 'Invalid email';
            }
          }
        }
        break;
    }

    setErrorObj(prev => ({ ...prev, [name]: error }));
    setFormObj(prev => ({ ...prev, [name]: value }));
  };

  const disabled = !isFormValid(
    formObj,
    errorObj,
    REQUIRED_FIELDS.ADD_RECIPIENT,
  );

  return (
    <Modal $maxWidth="714" open>
      <ModalHeader>
        <span className="modal-header">Add Recipient</span>
        <Cross
          onClick={onClose}
          data-event-name="Form_AddReipient_Icon_Close"
        />
      </ModalHeader>
      <ModalContent>
        <ModalDescription>
          <Form onSubmit={addRecipient}>
            <Form.Input
              fluid
              className="m-0"
              data-testid="emails"
              label="Enter Email Address"
              name="emails"
              error={errorObj.emails}
              value={formObj.emails}
              onChange={handleChange}
            />
            <Text color="bodyLight" className="mt-1 mb-4">
              Maximum of 10 recipients are allowed. Multiple email IDs can be
              seperated using a comma.
            </Text>

            <Text color="bodyLight" className="mb-1">
              Select Email Categories
            </Text>
            <Text variant="b12" color="bodyLight" className="mb-2">
              <a
                href={KNOW_MORE.EMAIL_NOTIFICATIONS}
                data-event-name="Link"
                target="_blank"
              >
                Know more
              </a>{' '}
              about email categories
            </Text>
            <Grid className="px-2">
              <Row columns={3}>
                {Object.keys(data).map(id => (
                  <Column key={id} className="p-0">
                    <List.Item>
                      <List.Content className="pt-1 pr-1">
                        <Checkbox
                          data-event-name={`Checkbox_${data[id].name}`}
                          label={data[id].name}
                          defaultChecked
                          onChange={() => handleToggle(id)}
                        />
                      </List.Content>
                    </List.Item>
                  </Column>
                ))}
              </Row>
            </Grid>

            <BtnContainer className="mt-4">
              <Button
                as="a"
                link
                onClick={onClose}
                data-event-name="Form_AddReipient_SecondaryButton"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="ml-4"
                primary
                data-event-name="Form_AddReipient_SecondaryButton"
                disabled={disabled}
              >
                Submit
              </Button>
            </BtnContainer>
          </Form>
        </ModalDescription>
      </ModalContent>
    </Modal>
  );
};

AddRecipientModal.propTypes = {
  data: PropTypes.object.isRequired,
  selectedAccountId: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AddRecipientModal;
